<template>
  <MainFormLayout layout="two-column">
    <section class="mb-20">
      <h1
        id="authorInformationTitle"
        class="font-bitter text-3xl font-bold mb-4">
        Authors
      </h1>
      <ol class="list-decimal pl-12 pr-4 mb-4">
        <li>
          Provide the <strong>author name</strong>, <strong>email address</strong> and
          <strong>affiliations</strong> for ALL authors listed on the article.
        </li>
        <li>
          Select the <strong>corresponding author</strong> who will be the primary contact for all communications
          related to this submission.
        </li>
      </ol>
    </section>

    <AuthorList :showValidationErrors="showValidationErrors"/>

    <PageValidationErrors
      v-if="showErrors"
      :failed-rules="failedRules"
      variant="error"
      class="mt-24">
    </PageValidationErrors>

    <ButtonComponent
      :label="nextText"
      variant="primary"
      data-testid="go-to-reviewer-information-page"
      id="authorNext"
      @customClick="next"
    />
  </MainFormLayout>
</template>

<script setup lang="ts">
import { VerifyResult } from '@/types'
import { computed, onMounted, ref } from 'vue'
import AuthorList from '../components/author/AuthorList.vue'
import PageValidationErrors from '../components/common/PageValidationErrors.vue'
import MainFormLayout from '../layouts/MainFormLayout.vue'
import { useStore } from 'vuex'
import router from '../routes'
import validateAuthorInfoView from '../services/validation/authorInfo'
import { useRoute } from 'vue-router'
import ButtonComponent from '../components/ui-components/ButtonComponent.vue'
import { trackEvent } from '../services/gtm'

const store = useStore()
const route = useRoute()
const showValidationErrors = ref(false)
const nextText = ref(store.state.finalReviewVisited ? 'Update and review' : 'Next')

const nextDestination = computed((): string => {
  return store.state.finalReviewVisited ? 'final-review' : 'review-editorial'
})

const verifyResult = computed<VerifyResult | null>(() => {
  return store.getters.getVerifyResultByName('authorInfoVerifyResult') as VerifyResult | null
})

const failedRules = computed(() => verifyResult.value?.failedRules ?? {})

const canProgress = computed((): boolean => {
  return verifyResult.value?.valid ?? false
})

const showErrors = computed((): boolean => {
  return showValidationErrors.value && hasValidationErrors()
})

onMounted(() => {
  store.commit('_setStep', route.meta.step)
})

const next = async () => {
  await validateAuthorInfoView(store)

  trackEvent({
    event: 'AuthorInfoValidationWarnings',
    category: 'Author Info Validation Warnings',
    action: 'click',
    authorInfoValidationWarnings: verifyResult.value?.errors ?? [],
  })

  if (canProgress.value) {
    router.push({ name: nextDestination.value })
  }
  else {
    showValidationErrors.value = true
  }
}

function hasValidationErrors() {
  return (verifyResult.value?.errors?.length ?? 0) !== 0
}</script>
